import { ScaffoldDropZone } from "../../../scaffold";
import { AnnotationVariable } from "../../contexts";
import { isAnnotationVariable } from "../../typeGuards/isAnnotationVariable";
import {
  ARSL_ADD_VARIABLES_TO_COLUMN,
  ARSL_APPLY_DROPPED_COLUMNS,
  ARSL_DEFAULT_COLUMN_WIDTH,
  type ArslActionAddVariablesToColumn,
  type ArslActionApplyDroppedColumns,
  type ArsListingColumn,
  ArsListingVariableEnum,
  BOTTOM_HEADER_ZONES,
  EMPTY_HEADER_COL_ID,
  type HeaderDropAction,
  LEFT_HEADER_ZONES,
  RIGHT_HEADER_ZONES,
} from "../interface";

/**
 * Parses a JSON drop payload string into an array of ArsListing variables.
 *
 * @param jsonPayload - Raw JSON string from a DataTransfer object
 * @returns Parsed variable list, or an empty array when the payload is invalid
 */
const parseVariables = (jsonPayload: string): AnnotationVariable[] => {
  try {
    const parsed = JSON.parse(jsonPayload);
    const parsedDataArray: unknown[] =
      typeof parsed === "object" ? (Array.isArray(parsed.data) ? parsed.data : [parsed.data]) : [];
    const variables: AnnotationVariable[] = parsedDataArray
      .map((item) => (item as { dropItem?: unknown }).dropItem)
      .filter(isAnnotationVariable);
    return variables;
  } catch {
    return [];
  }
};

/**
 * Returns the placeholder text string for a given variable data type.
 *
 * @param dataType - Variable data type string
 * @returns Placeholder text appropriate for the data type
 */
export const resolvePlaceholderText = (dataType: string): string => {
  switch (dataType.toLowerCase()) {
    case "boolean":
      return "true/false";
    case "datetime":
      return "01Jan2025 12:00";
    case "date":
      return "01Jan2025";
    case "time":
      return "12:00";
    case "integer":
    case "float":
    case "double":
    case "number":
    case "numeric":
    case "decimal":
      return "xx.x";
    default:
      return "xxxxx";
  }
};

/**
 * Maps a scaffold drop zone to a column placement action.
 *
 * @param zone - Classified drop zone within the target cell
 * @returns Column placement action
 */
const resolveDropAction = (zone: ScaffoldDropZone): HeaderDropAction => {
  if (LEFT_HEADER_ZONES.includes(zone)) {
    return "insert-before";
  } else if (RIGHT_HEADER_ZONES.includes(zone)) {
    return "insert-after";
  } else {
    return "replace";
  }
};

/**
 * Builds the payload for a drop action when the drop zone is one of the bottom zones.
 * Returns an `ArslActionAddVariablesToColumn` action that appends variables to the existing column.
 *
 * @param jsonPayload - Raw JSON string from a DataTransfer object
 * @param colId - Target scaffold column id where the drop occurred
 * @param zone - Bottom drop zone (BottomLeft, BottomEdgeLeft, BottomEdgeRight, or BottomRight)
 * @param textToT - Function to convert placeholder text to the column pattern type
 * @returns Add-variables-to-column action, or null when no valid variables are present
 */
export function buildApplyDroppedColumnsAction<T>(
  jsonPayload: string,
  colId: string,
  zone:
    | ScaffoldDropZone.BottomLeft
    | ScaffoldDropZone.BottomEdgeLeft
    | ScaffoldDropZone.BottomEdgeRight
    | ScaffoldDropZone.BottomRight,
  textToT: (text: string) => T,
): ArslActionAddVariablesToColumn | null;

/**
 * Builds the payload for a drop action when the drop zone is not a bottom zone.
 * Returns an `ArslActionApplyDroppedColumns` action that inserts or replaces columns.
 *
 * @param jsonPayload - Raw JSON string from a DataTransfer object
 * @param colId - Target scaffold column id where the drop occurred
 * @param zone - Non-bottom drop zone
 * @param textToT - Function to convert placeholder text to the column pattern type
 * @returns Apply-dropped-columns action, or null when no valid variables are present
 */
export function buildApplyDroppedColumnsAction<T>(
  jsonPayload: string,
  colId: string,
  zone: Exclude<
    ScaffoldDropZone,
    | ScaffoldDropZone.BottomLeft
    | ScaffoldDropZone.BottomEdgeLeft
    | ScaffoldDropZone.BottomEdgeRight
    | ScaffoldDropZone.BottomRight
  >,
  textToT: (text: string) => T,
): ArslActionApplyDroppedColumns<T> | null;

/**
 * Builds the payload for an ArslDispatch action from a variable drop event.
 * Parses the JSON payload, resolves the drop zone, and returns the appropriate action:
 * - Bottom zones (BottomLeft, BottomEdgeLeft, BottomEdgeRight, BottomRight) return an ArslActionAddVariablesToColumn,
 *   appending variables to the existing column without replacing it.
 * - All other zones return an ArslActionApplyDroppedColumns, inserting or replacing columns
 *   based on the resolved placement action.
 * Returns null when the payload contains no valid variables.
 *
 * @param jsonPayload - Raw JSON string from a DataTransfer object
 * @param colId - Target scaffold column id where the drop occurred
 * @param zone - Classified drop zone within the target cell
 * @param textToT - Function to convert placeholder text to the column pattern type
 * @returns Action payload, or null when no valid variables are present in the payload
 */
export function buildApplyDroppedColumnsAction<T>(
  jsonPayload: string,
  colId: string,
  zone: ScaffoldDropZone,
  textToT: (text: string) => T,
): ArslActionApplyDroppedColumns<T> | ArslActionAddVariablesToColumn | null;

export function buildApplyDroppedColumnsAction<T>(
  jsonPayload: string,
  colId: string,
  zone: ScaffoldDropZone,
  textToT: (text: string) => T,
): ArslActionApplyDroppedColumns<T> | ArslActionAddVariablesToColumn | null {
  const variables = parseVariables(jsonPayload);

  if (variables.length === 0) return null;

  if (BOTTOM_HEADER_ZONES.includes(zone) && colId !== EMPTY_HEADER_COL_ID) {
    return {
      operation: ARSL_ADD_VARIABLES_TO_COLUMN,
      colId,
      variables,
    };
  }

  const columns: ArsListingColumn<T>[] = variables.map((variable) => {
    const placeholderText = resolvePlaceholderText(variable.dataType);
    const displayPattern = [textToT(placeholderText)];
    const resultPattern = `{${variable.name.toLowerCase()}}`;
    const headerText = textToT(
      variable.description?.trim() !== "" ? variable.description : variable.name,
    );

    return {
      id: crypto.randomUUID(),
      columnWidth: ARSL_DEFAULT_COLUMN_WIDTH,
      displayPattern,
      resultPattern,
      headerText,
      variables: [
        {
          id: crypto.randomUUID(),
          dataset: variable.dataset,
          variable: variable.name,
          variableType: "displayOnly" as ArsListingVariableEnum,
        },
      ],
    };
  });

  return {
    operation: ARSL_APPLY_DROPPED_COLUMNS,
    colId,
    columns,
    dropAction: resolveDropAction(zone),
  };
}
